import React, { useEffect, useRef, useState } from "react";
import { Camera, MapPin, House, CheckCircle, FilePdf, ShareNetwork, PencilSimpleLine } from "@phosphor-icons/react";

/**
 * HeroSopralluogoMockup — mockup iPhone che mostra il "wow moment" del
 * Sopralluogo Express: l'agente è in casa, parla, scatta foto, e Pronto
 * registra/trascrive + GENERA TUTTO (annuncio, brochure, story, caption).
 *
 * Animazione progressiva 7 step × 1.2s:
 *   1) Status bar + header pulsing
 *   2) Waveform si attiva
 *   3-4) Bolle trascrizione (3 totali)
 *   5) Foto thumbnail
 *   6) Pill verde "Scheda pronta"
 *   7) Lista "Pronto ha creato anche..." con 4 check verdi (annuncio,
 *      brochure, story IG, caption)
 *
 * Iter 199: telefono ridotto da 300 → 220px, aggiunti output (annuncio/
 * brochure/story/caption) per chiarire che Pronto fa TUTTO da una sola
 * registrazione. Tolto il mic button bottom (decorativo, mangiava spazio).
 */
export default function HeroSopralluogoMockup() {
  const rootRef = useRef(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // iter 306: bug — prima il dep era [step] e l'effect si ri-eseguiva ad
    // ogni setStep, il cleanup faceva `cancelled=true` e l'animazione si
    // bloccava al primo step. Ora il dep è [] (mount-only) così la catena
    // tick→tick→… arriva fino a step 7 senza interruzioni.
    let cancelled = false;
    let s = 0;
    const tick = () => {
      if (cancelled) return;
      s += 1;
      setStep(s);
      if (s < 7) setTimeout(tick, 1200);
    };
    const t = setTimeout(tick, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  const TRANSCRIPTS = [
    { txt: "Trilocale al terzo piano, palazzo signorile…", at: 1 },
    { txt: "95 mq, ristrutturato, parquet rovere.", at: 2 },
    { txt: "Prezzo richiesto: 245 mila.", at: 3 },
  ];

  const DELIVERABLES = [
    { Icon: PencilSimpleLine, label: "Annuncio · creato",  at: 5 },
    { Icon: FilePdf,          label: "Vetrina · pronta",   at: 5 },
    { Icon: ShareNetwork,     label: "Story IG · pronta",  at: 6 },
    { Icon: PencilSimpleLine, label: "Caption · 3 toni",   at: 6 },
  ];

  return (
    <div
      ref={rootRef}
      data-testid="hero-sopralluogo-mockup"
      className="max-w-[220px] mx-auto select-none"
    >
      <style>{`
        @keyframes sop-pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1 }
          50%      { transform: scale(1.3); opacity: 0.6 }
        }
        @keyframes sop-wave {
          0%, 100% { transform: scaleY(0.3) }
          50%      { transform: scaleY(1) }
        }
        @keyframes sop-bubble-in {
          from { opacity: 0; transform: translateY(6px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        @keyframes sop-pill-up {
          from { opacity: 0; transform: translateY(14px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        .sop-dot { animation: sop-pulse-dot 1.2s ease-in-out infinite }
        .sop-wave-bar { animation: sop-wave 0.9s ease-in-out infinite; transform-origin: center }
        .sop-bubble  { animation: sop-bubble-in 350ms cubic-bezier(0.22,1,0.36,1) both }
        .sop-pill    { animation: sop-pill-up 500ms cubic-bezier(0.22,1,0.36,1) both }
        @media (prefers-reduced-motion: reduce) {
          .sop-dot, .sop-wave-bar, .sop-bubble, .sop-pill { animation: none }
        }
      `}</style>

      {/* iPhone 17 Pro frame — modernizzato */}
      <div
        className="relative rounded-[44px] p-[3px]"
        style={{
          background: "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
          boxShadow: "0 22px 48px -16px rgba(28,28,26,0.45), 0 6px 12px -6px rgba(28,28,26,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <div
          className="rounded-[40px] overflow-hidden bg-[#FAFAF7] relative"
          style={{ aspectRatio: "9 / 19.5" }}
        >
          {/* Dynamic island grande iPhone 17 Pro */}
          <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[76px] h-[20px] bg-black rounded-full z-10" />
          {/* Specular highlight sottile */}
          <div className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[5]"
               style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05), transparent)" }} />

          {/* Status bar */}
          <div className="px-4 pt-[14px] pb-1 flex items-center justify-between text-[7.5px] font-semibold text-[#1C1C1A] relative z-[6]">
            <span>9:41</span>
            <span className="flex items-center gap-0.5">
              <span className="text-[6px]">●●●</span>
              <span className="text-[6px]">5G</span>
              <span>100</span>
            </span>
          </div>

          {/* App header */}
          <div className="px-2.5 pt-1.5 pb-2 flex items-center gap-1.5">
            <span className="text-[8px] text-[#7C8B6E] font-medium">←</span>
            <div className="flex-1 text-center">
              <div className="text-[6.5px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E]">
                Sopralluogo
              </div>
              <div className="text-[8px] text-[#1C1C1A] flex items-center justify-center gap-1 mt-0.5">
                <MapPin size={7} weight="fill" className="text-[#7C8B6E]" />
                Via Solferino, Brera
              </div>
            </div>
          </div>

          {/* LIVE recording bar */}
          <div className="mx-2.5 bg-white border border-[#E5E7E0] rounded-lg p-2 mb-2">
            <div className="flex items-center gap-1 mb-1.5">
              <span className="sop-dot w-1.5 h-1.5 rounded-full bg-[#E11D48]" aria-hidden="true" />
              <span className="text-[6.5px] uppercase tracking-[0.18em] font-bold text-[#E11D48]">
                Live · registrando
              </span>
              <span className="ml-auto text-[7px] font-mono font-semibold text-[#1C1C1A] tabular-nums">
                02:{String(Math.min(34 + step * 4, 58)).padStart(2, "0")}
              </span>
            </div>
            <div className="h-[18px] flex items-center gap-[1.5px]">
              {Array.from({ length: 18 }).map((_, i) => {
                const baseHeights = [0.4, 0.7, 0.5, 0.9, 0.6, 0.85, 0.45, 0.7, 0.95, 0.55, 0.8, 0.4, 0.65, 0.9, 0.5, 0.75, 0.55, 0.85];
                const h = baseHeights[i] * 18;
                return (
                  <span
                    key={i}
                    className="sop-wave-bar bg-[#7C8B6E]"
                    style={{
                      width: 2,
                      height: h,
                      borderRadius: 1,
                      animationDelay: `${(i % 7) * 0.12}s`,
                      animationPlayState: step >= 1 ? "running" : "paused",
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Trascrizione live (3 bolle) */}
          <div className="px-2.5 space-y-1 mb-2">
            <div className="text-[6px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-0.5">
              Trascrizione
            </div>
            {TRANSCRIPTS.map((t, i) => (
              <div
                key={i}
                className="sop-bubble bg-[#F1F4ED] border border-[#D8DECE] rounded-md px-1.5 py-1 text-[7.5px] leading-tight text-[#1C1C1A]"
                style={{
                  animationDelay: `${t.at * 0.18}s`,
                  visibility: step >= t.at ? "visible" : "hidden",
                }}
              >
                {t.txt}
              </div>
            ))}
          </div>

          {/* Foto */}
          <div className="px-2.5 mb-2">
            <div className="text-[6px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1 flex items-center gap-0.5">
              <Camera size={7} weight="fill" className="text-[#7C8B6E]" />
              Foto · 4
            </div>
            <div className="grid grid-cols-4 gap-[3px]">
              {[
                "/landing/cucina-after.jpg",
                "/landing/sala-after.jpg",
                "/landing/camera-after.jpg",
                "/landing/bagno-after.jpg",
              ].map((src, i) => (
                <div
                  key={i}
                  className="sop-bubble aspect-square rounded-sm overflow-hidden bg-[#EAE7DF]"
                  style={{
                    animationDelay: `${0.9 + i * 0.1}s`,
                    visibility: step >= 4 ? "visible" : "hidden",
                  }}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" draggable="false" />
                </div>
              ))}
            </div>
          </div>

          {/* Scheda pronta */}
          <div className="px-2.5 mb-2">
            <div
              className="sop-pill bg-[#7C8B6E] text-white rounded-md px-2 py-1.5 flex items-center gap-1.5 shadow-sm"
              style={{ visibility: step >= 5 ? "visible" : "hidden" }}
            >
              <House size={10} weight="fill" />
              <div className="flex-1 min-w-0">
                <div className="text-[7px] font-bold leading-none">Scheda pronta</div>
                <div className="text-[6px] opacity-85 mt-[2px] truncate">Trilocale · Brera · 95mq · €245k</div>
              </div>
            </div>
          </div>

          {/* Pronto ha creato anche... */}
          <div className="px-2.5 pb-3">
            <div className="text-[6px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1">
              Pronto ha creato
            </div>
            <div className="space-y-[3px]">
              {DELIVERABLES.map((d, i) => (
                <div
                  key={i}
                  className="sop-bubble flex items-center gap-1.5 bg-white border border-[#E5E7E0] rounded-md px-1.5 py-[5px]"
                  style={{
                    animationDelay: `${(d.at - 4) * 0.15 + i * 0.08}s`,
                    visibility: step >= d.at ? "visible" : "hidden",
                  }}
                >
                  <CheckCircle size={10} weight="fill" className="text-[#7C8B6E] shrink-0" />
                  <d.Icon size={9} weight="regular" className="text-[#3F3D38] shrink-0" />
                  <span className="text-[7px] font-medium text-[#1C1C1A] leading-none">
                    {d.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
